// retentionClient — 部屋の retention (tier / 失効日数) 案内の HTTP クライアント
// (retention-communication-2026-07-14.md P1)。
//
// owner-gated な dispatcher endpoint を 1 本叩く (= `Authorization: Bearer
// <admin_token>` 必須、読み取り専用):
//   `GET /admin/rooms/:id/retention`
//     → 200 `{ tier, idleDays, graceDays, policy }`
//        - anonymous → { tier:'anonymous', idleDays:7,  graceDays:null, policy }
//        - password  → { tier:'password',  idleDays:30, graceDays:null, policy }
//        - email     → { tier:'email',     idleDays:30, graceDays:7,    policy }
//     → 404 room_not_found / 401 / 403。
//
// `idleDays/graceDays` は **その部屋の現在 tier** の値 (P1 チップ用)。`policy` は
// tier 非依存で **常に全 tier の失効日数** (匿名 / パスワード / メール警告 + 猶予) を
// 含み、RoomSettingsModal が「はしご」を描いて現在 tier をハイライトするのに使う。
//
// **数値 (idleDays/graceDays/policy) は必ずこのレスポンスを使う** (server の
// RETENTION_DEFAULTS が単一情報源、web に 7/30 を直書きしない = 陳腐化防止)。
//
// outcome union で返し、期待される失敗 (401/403/404/network) では throw しない
// (pastCanvasClient と同流儀)。取得失敗時は呼び出し側でチップを黙って出さない。
// fetch は narrow な shape で注入可能 (= node vitest からスタブを渡せる)。

/** 案内対象の retention tier (server `RetentionTier` と一致)。 */
export type RetentionTier = 'anonymous' | 'password' | 'email';

/**
 * 全 tier の失効日数 (server `RetentionPolicy` と一致)。`RoomRetention.tier`
 * (= その部屋の現在地) に依らず**常に全 tier の値**を含む。RoomSettingsModal の
 * 「はしご」表示 (各 tier の日数を並べて現在地をハイライト) が使う。
 */
export interface RetentionPolicy {
  /** 匿名: 無アクセスで削除に至る日数。 */
  readonly anonymousDays: number;
  /** パスワード: 無アクセスで削除に至る日数。 */
  readonly passwordDays: number;
  /** email: 無アクセスで警告メールに至る日数。 */
  readonly emailWarnDays: number;
  /** email: 警告後の物理削除までの猶予日数。 */
  readonly emailGraceDays: number;
}

/** owner-gated `GET /admin/rooms/:id/retention` の 200 body。 */
export interface RoomRetention {
  readonly tier: RetentionTier;
  /** 無アクティブで削除 / (email は) 警告に至る日数 (tier 依存)。 */
  readonly idleDays: number;
  /** email: 警告後の物理削除までの猶予日数。それ以外は null。 */
  readonly graceDays: number | null;
  /** tier 非依存の全 tier 失効日数 (はしご表示用)。 */
  readonly policy: RetentionPolicy;
}

/**
 * Minimal `fetch` shape — pastCanvasClient `FetchFn` と同一。node テストで
 * スタブを渡せるよう DOM `fetch` の全 overload は要求しない。
 */
export type FetchFn = (
  input: string,
  init?: { signal?: AbortSignal; headers?: Record<string, string>; method?: string },
) => Promise<Response>;

const defaultFetchFn: FetchFn = (input, init) => {
  const f = globalThis.fetch as
    | ((url: string, init?: RequestInit) => Promise<Response>)
    | undefined;
  if (f === undefined) throw new Error('globalThis.fetch is not available');
  return f.call(globalThis, input, init as RequestInit | undefined);
};

export type FetchRoomRetentionOutcome =
  | { readonly outcome: 'ok'; readonly retention: RoomRetention }
  /** 401/403: 未オーナー (= 非オーナーが叩いた / token 失効)。 */
  | { readonly outcome: 'unauthorized' }
  /** 404/400: 部屋が無い / 削除済 / roomId 不正。 */
  | { readonly outcome: 'not-found' }
  /** network / 予期しない status / JSON 破損。 */
  | { readonly outcome: 'error' };

export interface FetchRoomRetentionInput {
  /** dispatcher base URL (`readEnv().dispatcherUrl`)。末尾 `/` は許容。 */
  readonly dispatcherUrl: string;
  readonly roomId: string;
  /** owner の admin_token (Bearer)。無指定は無認証で撃つ → 通常 401 → unauthorized。 */
  readonly adminToken?: string;
  readonly signal?: AbortSignal;
}

export interface RetentionClientDeps {
  readonly fetchFn?: FetchFn;
}

const isRetentionTier = (v: unknown): v is RetentionTier =>
  v === 'anonymous' || v === 'password' || v === 'email';

const isRetentionPolicy = (v: unknown): v is RetentionPolicy => {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.anonymousDays === 'number' &&
    typeof o.passwordDays === 'number' &&
    typeof o.emailWarnDays === 'number' &&
    typeof o.emailGraceDays === 'number'
  );
};

const isRoomRetention = (v: unknown): v is RoomRetention => {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    isRetentionTier(o.tier) &&
    typeof o.idleDays === 'number' &&
    (o.graceDays === null || typeof o.graceDays === 'number') &&
    isRetentionPolicy(o.policy)
  );
};

export const fetchRoomRetention = async (
  input: FetchRoomRetentionInput,
  deps: RetentionClientDeps = {},
): Promise<FetchRoomRetentionOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const baseUrl = input.dispatcherUrl.replace(/\/+$/, '');
  const url = `${baseUrl}/admin/rooms/${encodeURIComponent(input.roomId)}/retention`;

  const init: { signal?: AbortSignal; headers?: Record<string, string> } = {};
  if (input.signal !== undefined) init.signal = input.signal;
  if (input.adminToken !== undefined && input.adminToken.length > 0) {
    init.headers = { Authorization: `Bearer ${input.adminToken}` };
  }

  let res: Response;
  try {
    res = await fetchFn(url, init);
  } catch {
    return { outcome: 'error' };
  }
  if (res.status === 401 || res.status === 403) return { outcome: 'unauthorized' };
  if (res.status === 404 || res.status === 400) return { outcome: 'not-found' };
  if (res.status !== 200) return { outcome: 'error' };

  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    return { outcome: 'error' };
  }
  if (!isRoomRetention(raw)) return { outcome: 'error' };
  return { outcome: 'ok', retention: raw };
};
